/**
 * The metric suggestion list under the expression input.
 *
 * Presentational twin of the chat's `MentionDropdown`, and it borrows that
 * component's two load-bearing details: `onMouseDown` with `preventDefault` so
 * a click never takes focus out of the input, and `scrollIntoView` on the
 * highlighted option so arrow-key navigation stays visible in a scrolled list.
 *
 * Each row shows the LABEL the user recognises and the KEY that gets inserted,
 * because the point of the control is the gap between those two.
 */

import React, { useEffect, useRef } from "react";

import type { KpiMetricOption } from "./useKpiMetricSuggest";

export interface KpiMetricSuggestDropdownProps {
	results: KpiMetricOption[];
	highlightedIndex: number;
	visible: boolean;
	/** DOM id of the list — the input points `aria-controls` at it. */
	listId: string;
	/** Builds each option's id, so the input can name the active one. */
	optionId: (index: number) => string;
	onSelect: (option: KpiMetricOption) => void;
}

const KpiMetricSuggestDropdown: React.FC<KpiMetricSuggestDropdownProps> = ({
	results,
	highlightedIndex,
	visible,
	listId,
	optionId,
	onSelect,
}) => {
	const listRef = useRef<HTMLUListElement>(null);

	useEffect(() => {
		const options = listRef.current?.querySelectorAll<HTMLElement>(
			'[role="option"]',
		);
		options?.[highlightedIndex]?.scrollIntoView({ block: "nearest" });
	}, [highlightedIndex]);

	if (!visible || results.length === 0) return null;

	return (
		<ul
			ref={listRef}
			id={listId}
			role="listbox"
			aria-label="Matching metrics"
			className="absolute left-0 right-0 top-full z-10 mt-1 max-h-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
		>
			{results.map((option, index) => (
				<li
					key={option.key}
					id={optionId(index)}
					role="option"
					aria-selected={index === highlightedIndex}
					className={`cursor-pointer px-3 py-1.5 ${
						index === highlightedIndex
							? "bg-[#E6F2F1] text-[#004C4B]"
							: "hover:bg-gray-50"
					}`}
					onMouseDown={(event) => {
						// Keep the caret where it is: taking focus would move the
						// insertion point out from under the word being replaced.
						event.preventDefault();
						onSelect(option);
					}}
				>
					<div className="truncate text-sm font-medium text-text-base">
						{option.label}
					</div>
					<div className="truncate font-mono text-xs text-text-muted">
						{option.key}
					</div>
				</li>
			))}
		</ul>
	);
};

export default React.memo(KpiMetricSuggestDropdown);
